import { useQuery } from '@apollo/client'
import getSymbolFromCurrency from 'currency-symbol-map'
import { format } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'

import { CryptoIcon } from '../../components'
import { formatCryptoAmount, formatFiatAmount } from '../../lib/helpers'
import { GET_BANK_TRANSACTIONS, GET_CRYPTO_TRANSACTIONS } from './queries'

const getCurrencySymbol = (currency: string | null) => {
    if (!currency) return ''

    return getSymbolFromCurrency(currency) ?? currency
}

const getValue = (val: { value: number; currency: string }) => {
    const amount = val.value
        ? val.currency === 'USD'
            ? formatFiatAmount(val.value)
            : formatCryptoAmount(val.value)
        : '-'
    return `${getCurrencySymbol(val.currency)} ${amount}`
}

export interface UseTransactionsProps {
    /** Transactions type */
    type?: 'crypto' | 'bank'
    /** Filter transactions by account id */
    filter?: string[]
    /** Filter transactions by provider */
    providers?: string
    /** Amount of items per page */
    step?: number

    /** */
    startDate?: Date | undefined
    endDate?: Date | undefined
}

export function useTransactions({
    type,
    filter,
    providers,
    step = 1000,
    endDate,
    startDate,
}: UseTransactionsProps) {
    const [index, setIndex] = useState(1)
    const [transactions, setTransactions] = useState<any>([])
    const [nextToken, setNextToken] = useState(undefined)
    const [previousNextToken, setPreviousNextToken] = useState(undefined)
    const { data: bankData, loading: bankLoading } = useQuery(
        GET_BANK_TRANSACTIONS,
        {
            skip: type !== 'bank',
            variables: {
                input: {
                    limit: 1000,
                    nextToken,
                    accountIds: !filter?.length ? undefined : filter,
                    endDate,
                    startDate,
                    providers: filter?.length
                        ? undefined
                        : !providers?.length
                        ? undefined
                        : providers,
                },
            },
            fetchPolicy: 'network-only',
        },
    )

    const { data: cryptoData, loading: cryptoLoading } = useQuery(
        GET_CRYPTO_TRANSACTIONS,
        {
            skip: type !== 'crypto',
            variables: {
                input: {
                    limit: 1000,
                    nextToken,
                    endDate,
                    startDate,
                    accountIds: !filter?.length ? undefined : filter,
                    providers: !providers?.length ? undefined : providers,
                },
            },
            fetchPolicy: 'network-only',
        },
    )

    const hanldeCryptoTransactions = useCallback(() => {
        const transactionList =
            !cryptoLoading && cryptoData
                ? cryptoData.listCryptoTransactions.items
                : []

        return transactionList.map((t: any) => {
            return {
                ...t,
                amount: getValue(t.amount),
                fiat: getValue(t.fiat),
                category: t.category.join(', '),
                date: format(t.date, 'dd MMM, yyyy'),
                icon: (
                    <CryptoIcon
                        name={providers ? (providers as string) : 'BTC'}
                        size={24}
                        className='mr-4'
                        active={true}
                    />
                ),
            }
        })
    }, [cryptoData, cryptoLoading, providers])

    const handleBankTransactions = useCallback(() => {
        const transactionList =
            !bankLoading && bankData ? bankData.listBankTransactions.items : []

        return transactionList.map((t: any) => {
            return {
                ...t,
                amount: getValue(t.amount),
                date: format(new Date(t.date), 'dd MMM, yyyy hh:mm'),
            }
        })
    }, [bankData, bankLoading])

    useEffect(() => {
        if (bankData) {
            setPreviousNextToken(bankData.listBankTransactions.nextToken)
        } else {
            setPreviousNextToken(undefined)
        }
    }, [bankData, type])

    useEffect(() => {
        if (cryptoData) {
            setPreviousNextToken(cryptoData.listCryptoTransactions.nextToken)
        } else {
            setPreviousNextToken(undefined)
        }
    }, [cryptoData, type])

    const handleUpdateTransactions = useCallback(() => {
        if (type === 'bank') {
            setTransactions([...transactions, ...handleBankTransactions()])
        } else {
            setTransactions([...transactions, ...hanldeCryptoTransactions()])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        setTransactions,
        hanldeCryptoTransactions,
        handleBankTransactions,
        type,
    ])

    useEffect(() => {
        handleUpdateTransactions()
    }, [handleUpdateTransactions])

    const updateNextToken = useCallback(() => {
        setNextToken(previousNextToken)
    }, [setNextToken, previousNextToken])

    useEffect(() => {
        setNextToken(undefined)
        setTransactions([])
        setIndex(1)
    }, [filter, setNextToken, setTransactions, setIndex, endDate, startDate])

    useEffect(() => {
        if (
            bankData &&
            previousNextToken &&
            type === 'bank' &&
            transactions.length < index * step
        ) {
            updateNextToken()
        }
    }, [
        bankData,
        type,
        updateNextToken,
        previousNextToken,
        index,
        step,
        transactions.length,
    ])

    useEffect(() => {
        if (
            cryptoData &&
            previousNextToken &&
            type === 'crypto' &&
            transactions.length < index * step
        ) {
            updateNextToken()
        }
    }, [
        cryptoData,
        type,
        updateNextToken,
        previousNextToken,
        index,
        step,
        transactions.length,
    ])

    const handleNextPage = useCallback(() => {
        setIndex(index + 1)
        updateNextToken()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index])

    return {
        transactions,
        updateNextToken: handleNextPage,
        nextAvailable: !!previousNextToken,
        loading: bankLoading || cryptoLoading,
    }
}
